<template>
  <p-data-table
    v-bind="attrs"
    ref="dataTable"
    v-model:filters="filters"
    v-model:selection="selectedRows"
    v-model:expanded-rows="expandedRows"
    :value="tableData"
    :global-filter-fields="Object.keys(filters)"
    :row-class="getRowClass"
    filter-display="menu"
    :resizable-columns="!!props.columnSaveKey"
    column-resize-mode="expand"
    :loading="attrs.loading || columnsLoading"
    :pt="{
      table: {
        style: props.columnSaveKey ? 'table-layout: fixed;' : undefined
      },
      column: {
        filter: {
          style: 'margin-inline-start: 0;'
        }
      },
      header: {
        class: 'p-0'
      },
      tableContainer: {
        class: 'overflow-x-auto'
      }
    }"
    @column-resize-end="handleColumnResize"
  >
    <template #loading>
      <fluency-loader></fluency-loader>
    </template>

    <!-- Bulk actions menu (shown when bulkMenu is provided and rows are selected) -->
    <template #header>
      <div
        class="d-flex-center position-relative"
        :style="{ minHeight: headerMinHeight }"
      >
        <slot name="left-header"></slot>
        <Transition name="slide-down">
          <p-menu-bar
            v-if="bulkMenu && selectedRows.length > 0"
            :model="bulkMenu"
            :class="{ 'position-absolute w-100': hasHeaderContent, 'flex-grow-1': !hasHeaderContent }"
            :style="{ top: '0', left: '0', zIndex: '1' }"
            :dt="blueBar"
          >
            <template #start>
              <div class="selected-count mr-2 pr-5 border-right border-secondary">{{ selectedRows.length }} Selected</div>
            </template>
            <template #end>
              <p-button
                variant="link"
                class="p-0"
                @click="clearSelection"
              >
                <template #icon>
                  <fluency-icon type="close"></fluency-icon>
                </template>
              </p-button>
            </template>
          </p-menu-bar>
        </Transition>

        <!-- Table Actions Dropdown -->
        <t-dropdown
          v-if="props.columnSaveKey || slots['additional-table-actions'] || props.allowDownload"
          ref="tableActionDropdown"
          v-p-tooltip.left="`More Table Actions`"
          class="ml-auto pl-3 my-2"
          variant="text"
          no-caret
        >
          <template #button-content>
            <fluency-icon
              size="lg"
              type="ellipses"
            />
          </template>
          <li v-if="props.allowDownload">
            <t-button
              variant="text"
              class="btn-nav-menu-item justify-content-start"
              @click="downloadTable()"
              >Download</t-button
            >
          </li>

          <slot name="additional-table-actions"></slot>

          <li v-if="props.columnSaveKey">
            <t-button
              v-if="!onlyEditColumns"
              variant="text"
              class="justify-content-start btn-nav-menu-item"
              @click.stop.prevent="handleInlineColumnsToggle"
              >Edit Columns
              <fluency-icon
                type="chevronDown"
                :class="{ 'rotate-180': inlineColumnsOpen }"
            /></t-button>
          </li>
          <div
            v-if="inlineColumnsOpen || onlyEditColumns"
            class="pt-2 px-2"
          >
            <column-edit
              :default-fields="props.fields"
              :column-key="props.columnSaveKey"
              @update="handleColumnOrderUpdate"
            />
          </div>
        </t-dropdown>
      </div>
    </template>

    <!-- Selection column (shown when selectedRowKey is provided) -->
    <p-column
      v-if="selectedRowKey"
      selection-mode="multiple"
      header-style="width: 3rem"
    />

    <p-column
      v-if="slots['row-details'] || slots.expansion"
      expander
      header-style="width: 3rem"
      :body-class="expanderBodyClass"
    />

    <p-column
      v-for="field in normalizedFields"
      :key="field.key || field.slot"
      :field="field.key"
      :header="field.label"
      :sortable="field.sortable"
      :data-type="field.dataType"
      :sort-field="field.sortByFormatted ? (data) => field.formatter(data[field.key], field, data) : undefined"
      :style="getColumnStyle(field)"
      :class="field.class"
      :header-class="[field.headerClass, `column-key-${field.key}`]"
      :body-class="field.tdClass"
      :show-filter-operator="filters[field.key]?.constraints?.length > 1"
    >
      <!-- Column Header Slot -->
      <template
        v-if="hasHeaderSlot(field)"
        #header
      >
        <slot
          :name="getHeaderSlotName(field)"
          :label="field.label"
          :field="field"
          :column="field"
        >
          {{ field.label }}
        </slot>
      </template>

      <!-- Column Body/Cell Slot -->
      <template
        v-if="hasCellSlot(field) || field.formatter"
        #body="slotProps"
      >
        <slot
          :name="getCellSlotName(field)"
          :value="getCellValue(slotProps.data, field)"
          :item="slotProps.data"
          :field="field"
          :index="slotProps.index"
        >
          {{ formatCellValue(slotProps.data, field) }}
        </slot>
      </template>

      <!--       filters -->
      <template
        v-if="field.filter"
        #filter="{ filterModel }"
      >
        <template
          v-for="filterType in field.filterOptions"
          :key="filterType"
        >
          <p-input-text
            v-if="filterType === 'string'"
            v-model="filterModel.value"
          />
          <t-radio-group
            v-else-if="Array.isArray(filterType)"
            v-model="filterModel.value"
            stacked
            :options="filterType"
          />
          <p-input-number
            v-else-if="filterType === 'numeric'"
            v-model="filterModel.value"
          />
          <single-date-picker
            v-else-if="filterType === 'date'"
            :model-value="filterModel.value"
            embed
            min-date="2021-01-01"
            @update:modelValue="filterModel.value = moment($event).toDate()"
          />
        </template>
      </template>
      <template
        v-if="field.filter"
        #filtericon
      >
        <fluency-icon
          type="filter"
          :class="{ 'has-filters': filters[field.key].constraints.some((constraint) => constraint.value !== null) }"
          style="top: 0"
        ></fluency-icon>
      </template>
    </p-column>

    <!-- Pass through other DataTable slots -->
    <template
      v-if="slots.empty"
      #empty
    >
      <slot name="empty" />
    </template>

    <template
      v-if="slots.expansion || slots['row-details']"
      #expansion="slotProps"
    >
      <slot
        name="expansion"
        :data="slotProps.data"
        :index="slotProps.index"
      />
      <slot
        name="row-details"
        :item="slotProps.data"
        :index="slotProps.index"
        :toggle-details="() => toggleRowExpansion(slotProps.data)"
      />
    </template>
  </p-data-table>
</template>

<script setup>
import { computed, useSlots, useAttrs } from 'vue'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import singleDatePicker from 'backpack/src/components/common/dateSelector/components/singleDatePicker.vue'
import ColumnEdit from 'core-ui/components/common/columnEdit.vue'
import { useTableSelection } from 'core-ui/composables/useTableSelection.js'
import { useSavedTableColumns } from '../../composables/useSavedTableColumns'
import moment from 'moment'
import _debounce from 'lodash-es/debounce'
import FluencyLoader from '../common/fluencyLoader.vue'

const { $filters } = useNuxtApp()
const props = defineProps({
  /**
   * Array of field definitions compatible with Bootstrap-Vue table format
   * Each field object can contain:
   * - key: string - Property name in the data
   * - label: string - Column header text
   * - sortable: boolean - Enable sorting
   * - formatter: function - Format cell value for display
   * - sortByFormatted: function - Custom sort logic
   * - filterByFormatted: function - Custom filter logic
   * - tdClass: string - CSS class for cells
   * - thClass: string - CSS class for headers
   * - class: string - CSS class for column
   * - headerClass: string - CSS class for header
   * - slot: string - Custom slot name
   * - defaultWidth: number - Default column width
   * - sticky: boolean - Make column sticky
   * - resizable: boolean - Allow column resizing
   */
  fields: {
    type: Array,
    required: true
  },

  /**
   * Table data array (PrimeVue uses 'value' prop)
   */
  value: {
    type: Array,
    default: () => []
  },

  /**
   * Alternative prop name for table data (Bootstrap-Vue compatibility)
   */
  items: {
    type: Array,
    default: () => []
  },

  /**
   * Unique key for storing selected rows (enables row selection when provided)
   */
  selectedRowKey: {
    type: String,
    default: null
  },

  /**
   * Unique key for saving row order and width (enables both features when provided)
   */
  columnSaveKey: {
    type: String,
    default: null
  },

  /**
   * Array of PrimeVue MenuItem objects for bulk actions menu
   * Menu is shown in the header when rows are selected
   */
  bulkMenu: {
    type: Array,
    default: null
  },
  allowDownload: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['toggle-details'])
const slots = useSlots()
const attrs = useAttrs()
const dataTable = ref(null)
const filters = ref({})
const expandedRows = ref([])
const inlineColumnsOpen = ref(false)
const tableActionDropdown = ref(null)
const normalizedFields = ref([])
const blueBar = ref({
  root: {
    background: 'var(--p-bionic-indigo-50)',
    border: {
      color: 'var(--p-bionic-indigo-100)'
    },
    item: {
      focus: {
        background: 'white'
      }
    }
  }
})
const { savedOrder, savedWidths, saveColumnWidth, saveColumnOrder, columnsLoading } = useSavedTableColumns(props.columnSaveKey)

// Row selection support
const { selection, setSelection, clearSelection } = props.selectedRowKey
  ? useTableSelection(props.selectedRowKey)
  : { selection: ref([]), setSelection: () => {}, clearSelection: () => {} }
const onlyEditColumns = computed(() => props.columnSaveKey && !props.allowDownload && !slots['additional-table-actions'])
const selectedRows = computed({
  get: () => selection.value,
  set: (value) => setSelection(value)
})
const hasHeaderContent = computed(
  () => !!(props.columnSaveKey || slots['additional-table-actions'] || slots['header-left'] || slots['header-right'] || props.allowDownload)
)
const headerMinHeight = computed(() => (hasHeaderContent.value && props.bulkMenu ? '48px' : '0'))
const expanderBodyClass = (rowData) => {
  console.log(rowData)
  return ''
}

// Normalize fields array to ensure all fields have required properties
const normalizeFields = _debounce(() => {
  let enhancedFields = props.fields.map((field) => {
    // Support both string and object field definitions
    if (typeof field === 'string') {
      return {
        key: field,
        label: field,
        sortable: false
      }
    }

    return {
      ...field,
      key: field.key,
      label: field.label || $filters.convertFromCamelCase(field.key),
      sortable: field.sortable || field.filter || false,
      formatter: field.formatter,
      sortByFormatted: field.sortByFormatted,
      filterByFormatted: field.filterByFormatted,
      tdClass: field.tdClass || field.class,
      headerClass: field.thClass || field.headerClass,
      class: field.class,
      slot: field.slot,
      defaultWidth: savedWidths?.value?.[field.key] || field.defaultWidth,
      sticky: field.sticky,
      resizable: field.resizable,
      filter: field.filter,
      filterOptions: field.filterOptions
    }
  })
  if (savedOrder?.value) {
    const { columnList } = savedOrder.value
    enhancedFields = enhancedFields.filter((field) => {
      const index = columnList.findIndex((so) => so.columnName === field.key)
      return index === -1 || columnList[index].visible === true
    })
    enhancedFields.sort((a, b) =>
      columnList.find((so) => a.key === so.columnName).columnOrder > columnList.find((so) => b.key === so.columnName).columnOrder ? 1 : -1)
  }
  normalizedFields.value = enhancedFields
}, 100)

// Get actual table data (support both 'value' and 'items' props)
const tableData = computed(() => {
  return props.value?.length ? props.value : props.items
})

// Check if a header slot exists for a field
const hasHeaderSlot = (field) => {
  const slotName = getHeaderSlotName(field)
  return !!slots[slotName]
}

// Check if a cell slot exists for a field
const hasCellSlot = (field) => {
  const slotName = getCellSlotName(field)
  return !!slots[slotName]
}

// Get header slot name in Bootstrap-Vue format: head(key) or head()
const getHeaderSlotName = (field) => {
  if (field.slot) {
    return `head(${field.slot})`
  }
  return field.key ? `head(${field.key})` : 'head()'
}

// Get cell slot name in Bootstrap-Vue format: cell(key)
const getCellSlotName = (field) => {
  if (field.slot) {
    return `cell(${field.slot})`
  }
  return `cell(${field.key})`
}

// Get value from data object for a field
const getCellValue = (data, field) => {
  if (!data || !field.key) return null

  // Support nested properties with dot notation
  const keys = field.key.split('.')
  let value = data

  for (const key of keys) {
    if (value && typeof value === 'object') {
      value = value[key]
    } else {
      return null
    }
  }

  return value
}

// Format cell value using field formatter if provided
const formatCellValue = (data, field) => {
  const value = getCellValue(data, field)

  if (field.formatter && typeof field.formatter === 'function') {
    return field.formatter(data[field.key], field, data)
  }

  return value
}

// Get column style based on field configuration
const getColumnStyle = (field) => {
  const styles = field.styles || {}

  if (field.defaultWidth) {
    styles.width = `${field.defaultWidth}px`
  }

  if (field.sticky) {
    styles.position = 'sticky'
    styles.left = '0'
    styles.zIndex = '1'
  }

  return styles
}

const getRowClass = (row) => {
  return [{ 'row-expanded': expandedRows.value[row[attrs['data-key']]] }]
}

// Toggle row expansion (for row-details compatibility)
const toggleRowExpansion = (rowData) => {
  // This would need to be implemented based on PrimeVue's expandedRows model
  // Emitting event for parent to handle
  emit('toggle-details', rowData)
}

const handleColumnResize = (event) => {
  console.log(event)
  const width = event.element.scrollWidth
  let key
  event.element.classList.forEach((className) => {
    if (className.indexOf('column-key-') === 0) {
      key = className.replace('column-key-', '')
    }
  })
  saveColumnWidth(key, width)
  normalizeFields()
}

const handleInlineColumnsToggle = () => {
  inlineColumnsOpen.value = !inlineColumnsOpen.value
  tableActionDropdown?.value?.alignDropdown()
}

const handleColumnOrderUpdate = (data) => {
  saveColumnOrder(data)
  normalizeFields()
}

const downloadTable = () => {
  dataTable.value.exportCSV()
}

watch(
  () => props.fields,
  (value) => {
    const f = {}
    for (const field of value) {
      if (field.filter) {
        f[field.key] = {
          operator: FilterOperator.AND,
          showOperator: false,
          constraints: field.filterOptions.map((option) => ({
            value: null,
            matchMode: option === 'numeric' ? FilterMatchMode.EQUALS : option === 'date' ? FilterMatchMode.DATE_IS : FilterMatchMode.CONTAINS
          }))
        }
      }
    }
    filters.value = f
  },
  { immediate: true }
)

watch(
  [() => savedOrder?.value, savedWidths?.value],
  () => {
    normalizeFields()
  },
  { immediate: true }
)
// Expose methods for parent components if needed
defineExpose({
  tableData,
  normalizedFields
})
</script>

<style scoped lang="scss">
.has-filters {
  color: $bionic-primary-300;
  :deep(svg) {
    fill: $bionic-primary-300;
  }
}
:deep(.table-hover-icon-btn) {
  visibility: hidden;
}
:deep(.p-datatable-tbody > tr:not(.p-datatable-row-expansion):hover .table-hover-icon-btn) {
  visibility: visible;
}
:deep(.table-hover-icon-btn:focus) {
  visibility: visible;
}
:deep(.row-expanded) {
  background-color: $bionic-primary-50;
  td:first-child {
    border-left: 4px solid $bionic-primary-300;
  }
  > td {
    border-bottom: 0;
  }
}
:deep(.p-datatable-row-expansion) {
  > td:first-child {
    border-left: 4px solid $bionic-primary-300;
  }
}
:deep(.p-datatable-mask) {
  background: rgba(252, 252, 253, 0.6) !important;
}
// Slide down transition for bulk menu
.slide-down-enter-active,
.slide-down-leave-active {
  transition:
    max-height 0.4s ease-out,
    opacity 0.3s ease-out;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  max-height: 200px;
  opacity: 1;
}
</style>
